import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Wind, History, LogOut, Shield, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/utils/constants';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [recentSignups, setRecentSignups] = useState(0);
  // Client-side "seen" timestamp so the admin badge resets when the admin visits /admin.
  const [signupsSeenAt, setSignupsSeenAt] = useState(() => {
    try { return localStorage.getItem('admin_signups_seen_at') || ''; } catch { return ''; }
  });

  const markSignupsSeen = () => {
    const nowIso = new Date().toISOString();
    try { localStorage.setItem('admin_signups_seen_at', nowIso); } catch { /* ignore */ }
    setSignupsSeenAt(nowIso);
    setRecentSignups(0);
  };

  // Poll admin counters — refreshes every 60s and on route change.
  useEffect(() => {
    if (!user?.is_admin) { setPendingCount(0); setRecentSignups(0); return; }
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const [pend, recent] = await Promise.all([
          axios.get(`${API}/admin/pending-users`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/stats/recent-signups`).catch(() => ({ data: { count: 0, users: [] } })),
        ]);
        if (cancelled) return;
        setPendingCount(Array.isArray(pend.data) ? pend.data.length : 0);
        // Filter signups against the "seen" timestamp so the badge resets after the admin has visited.
        const users = Array.isArray(recent.data?.users) ? recent.data.users : [];
        const seenAt = signupsSeenAt || '';
        const unseen = seenAt
          ? users.filter((u) => (u.created_at || '') > seenAt).length
          : (recent.data?.count || 0);
        setRecentSignups(unseen);
      } catch { /* silent — nav must never break */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.is_admin, location.pathname, signupsSeenAt]);

  const navItems = [
    { path: '/breathing', icon: Wind, label: 'Respirer' },
    { path: '/mon-espace', icon: UserRound, label: 'Mon Espace' },
    { path: '/history', icon: History, label: 'Historique' },
  ];

  if (user?.is_admin) {
    // Badge shows total actionable items: pending approvals + new sign-ups in the last 24h
    navItems.push({ path: '/admin', icon: Shield, label: 'Admin', badge: pendingCount + recentSignups });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Déconnexion réussie');
  };

  const activeStyle = {
    background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
    color: '#FFFFFF',
    boxShadow: '0 4px 14px -2px rgba(14,165,233,0.55), inset 0 1px 0 rgba(255,255,255,0.30)',
    textShadow: '0 1px 2px rgba(0,0,0,0.25)',
  };

  const inactiveTextStyle = {
    color: '#1E293B',
    textShadow: '0 1px 2px rgba(255,255,255,0.75)',
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50"
      data-testid="main-navigation"
      style={{
        background: 'rgba(255,255,255,0.38)',
        backdropFilter: 'saturate(180%) blur(28px)',
        WebkitBackdropFilter: 'saturate(180%) blur(28px)',
        borderTop: '1px solid rgba(255,255,255,0.45)',
        boxShadow: '0 -8px 32px rgba(15,32,64,0.10)',
        // Keep just enough bottom padding so the iOS home indicator doesn't sit
        // exactly on the icons. We DON'T use the full safe-area-inset-bottom
        // because that leaves a visually empty band under the nav.
        paddingBottom: 'max(calc(env(safe-area-inset-bottom, 0px) / 2), 4px)',
      }}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-center lg:justify-between gap-3 px-3 py-3">
        {/* Desktop-only brand mark, sits at the far left of the bottom bar */}
        <div className="hidden lg:flex items-center gap-3 pl-2 pr-4 select-none" aria-hidden="true">
          <img
            src="/logo-mains-nuages.png"
            alt="Physiothérapie Les Mains Nuages"
            className="h-9 w-auto flex-shrink-0"
            style={{ filter: 'drop-shadow(0 1px 2px rgba(15,32,64,0.15))' }}
            data-testid="nav-brand-logo"
          />
        </div>

        <div className="flex flex-1 items-center justify-around gap-1 max-w-2xl mx-auto lg:mx-0 lg:justify-end lg:gap-2">
        {navItems.map(({ path, icon: Icon, label, badge }) => {
          const isActive = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => {
                if (path === '/admin') markSignupsSeen();
                navigate(path);
              }}
              className="relative flex flex-1 flex-col items-center justify-center gap-1 py-2 px-3 rounded-2xl transition-all duration-300"
              style={isActive ? activeStyle : inactiveTextStyle}
              data-testid={`nav-${label.toLowerCase()}`}
            >
              <div className="relative">
                <Icon
                  size={22}
                  strokeWidth={isActive ? 2.4 : 2.1}
                  style={{ filter: isActive ? 'none' : 'drop-shadow(0 1px 1px rgba(255,255,255,0.7))' }}
                />
                {badge > 0 && (
                  <span
                    className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center leading-none animate-pulse"
                    style={{
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      boxShadow: '0 2px 6px rgba(220,38,38,0.55), 0 0 0 2px rgba(255,255,255,0.9)',
                    }}
                    data-testid={`nav-${label.toLowerCase()}-badge`}
                    aria-label={`${badge} nouvelle${badge > 1 ? 's' : ''} demande${badge > 1 ? 's' : ''}`}
                  >
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </div>
              <span
                className="text-[11px] sm:text-xs font-semibold tracking-wide"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {label}
              </span>
            </button>
          );
        })}
        <button
          onClick={handleLogout}
          className="flex flex-1 flex-col items-center justify-center gap-1 py-2 px-3 rounded-2xl transition-all duration-300 hover:text-rose-500"
          style={inactiveTextStyle}
          data-testid="nav-logout"
        >
          <LogOut size={22} strokeWidth={2.1} style={{ filter: 'drop-shadow(0 1px 1px rgba(255,255,255,0.7))' }} />
          <span
            className="text-[11px] sm:text-xs font-semibold tracking-wide"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Sortir
          </span>
        </button>
        </div>
      </div>

      {/* Mobile-only brand strip — sits inside the translucent nav surface, just
          below the tab buttons and above the iOS home indicator (safe-area).
          Provides a consistent brand anchor on every app page without covering
          the content. Hidden on desktop (logo already at the far-left of the nav).
          Also hidden on the Défis (challenge-mode) screen — the horizontal
          logo is displayed at the top of the page instead. This is toggled
          via `body[data-challenge-mode="true"]` (see index.css). */}
      <div
        className="nav-mobile-brand-strip lg:hidden flex items-center justify-center gap-2 pt-1 pb-1 px-6"
        aria-hidden="true"
        data-testid="nav-mobile-brand-strip"
      >
        <img
          src="/logo-mains-nuages.png"
          alt="Physiothérapie Les Mains Nuages"
          className="h-6 w-auto opacity-85"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(15,32,64,0.18))' }}
          data-testid="nav-mobile-brand-logo"
        />
        <p
          className="text-[10px] font-medium tracking-wide text-slate-700/90 whitespace-nowrap"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Julien Jaccard © 2026
        </p>
      </div>
    </nav>
  );
};

export default Navigation;
